/**
 * The five guards.
 *
 * Order is fixed, and each guard assumes every guard above it has passed — which is what
 * lets them stay one condition each:
 *
 *   1  RequireAuth      no GCIP session          -> /login?next=<path>
 *   2  RequireVerified  me.emailVerified false   -> /verify
 *   3  RequireOrg       me.memberships empty     -> /onboarding
 *   4  RequireAdmin     role !== 'admin'         -> / + "Billing is managed by your team admin."
 *   5  RequirePlan      plan < enterprise        -> /team  (v1.1)
 *   +  RequireTeamPlan  plan === 'individual'    -> /  (no toast: Team is not a thing an individual
 *                                                     was denied, it is a thing they do not have)
 *
 * Auth before verified, or RequireVerified reads `me` from an unauthenticated session.
 * Verified before org, or an unverified team signup creates an org for an address nobody has
 * proven they control. Org before admin, or RequireAdmin reads a role from a membership that
 * does not exist.
 *
 * `status = locked` is NOWHERE in this list. It is a content state, not a routing state:
 * routing a locked customer away from /billing would leave them no way to pay us.
 *
 * Every guard is UX. None of it is enforcement — a member who forges a request still gets a
 * 403. Hiding a control is a courtesy to the user, never a control on the user.
 */
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useToast } from "@promptgatewaysadmin/ui";
import { useEffect } from "react";
import { useAuth } from "../auth/AuthContext.js";
import { useOptionalSession } from "../auth/SessionProvider.js";
import { currentPath, safeNext } from "../auth/safeNext.js";
import { isKnownInterval, isKnownPlan } from "../pricing.js";
import { ExistingAccountRedirect } from "../routes/auth/ExistingAccountRedirect.js";

const PLAN_RANK: Record<string, number> = {
  individual: 0,
  team: 1,
  enterprise: 2,
};

export function RequireAuth() {
  const auth = useAuth();
  const location = useLocation();
  if (auth.status === "loading") return null;
  if (auth.status === "unauthenticated") {
    // Never a bare /login: the destination is what makes the round trip survivable. The
    // guard writes only our own path — the read side validates it again.
    const next = encodeURIComponent(currentPath(location));
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return <Outlet />;
}

export function RequireVerified() {
  const session = useOptionalSession();
  if (!session) return null;
  if (!session.emailVerified) return <Navigate to="/verify" replace />;
  return <Outlet />;
}

export function RequireOrg() {
  const session = useOptionalSession();
  if (!session) return null;
  if (!session.hasOrg) return <Navigate to="/onboarding" replace />;
  return <Outlet />;
}

export function RequireAdmin() {
  const session = useOptionalSession();
  const toast = useToast();
  const isMember = Boolean(session) && session!.role !== "admin";

  // The toast is a side effect of the redirect, so it fires once rather than on every
  // render of the guard.
  useEffect(() => {
    if (isMember) toast.show("Billing is managed by your team admin.");
  }, [isMember, toast]);

  if (!session) return null;
  if (isMember) return <Navigate to="/" replace />;
  return <Outlet />;
}

/**
 * An individual-plan org has no team. /team and its sub-routes are hidden from the nav
 * (AppShell) and redirected here; a member of a team who lost admin is a different case and
 * goes through RequireAdmin.
 */
export function RequireTeamPlan() {
  const session = useOptionalSession();
  if (!session) return null;
  if (session.plan === "individual") return <Navigate to="/" replace />;
  return <Outlet />;
}

export function RequirePlan({ min }: { min: "team" | "enterprise" }) {
  const session = useOptionalSession();
  const toast = useToast();
  const rank = PLAN_RANK[session?.plan ?? "individual"] ?? 0;
  const blocked = Boolean(session) && rank < (PLAN_RANK[min] ?? 0);

  useEffect(() => {
    if (blocked) toast.show("SSO and SCIM are on Enterprise — talk to sales.");
  }, [blocked, toast]);

  if (!session) return null;
  if (blocked) return <Navigate to="/team" replace />;
  return <Outlet />;
}

/**
 * The inverse guard for /login and /signup. A half-finished signup is not a reason to show
 * a signed-in user a signup form.
 *
 * /signup NEVER forwards a signed-in user to /checkout. Every "Create a free account" /
 * "Start free trial" link in the fleet (extension, website CTAs and pricing cards, lifecycle
 * emails) points at /signup?plan=…, so a signed-in visitor there is someone who already has
 * an account, not someone who chose to buy. Forwarding them to checkout put a payment form in
 * front of paying customers, and paying it opened a second Stripe charge. Paying is an
 * explicit Upgrade / Choose plan from inside the portal. Billing refuses the second charge
 * with 409 ALREADY_SUBSCRIBED regardless — this is the courtesy, that is the control.
 * Where they land is ExistingAccountRedirect's call (it lives with the routes, so this file
 * stays free of billing status — see routing.test.tsx).
 *
 * /login keeps its behaviour: a safe `next`, or the plan it was handed.
 */
export function RedirectIfAuthenticated({
  children,
  signup = false,
}: {
  children: React.ReactNode;
  signup?: boolean;
}) {
  const auth = useAuth();
  const location = useLocation();
  const session = useOptionalSession();
  if (auth.status === "loading") return null;
  if (auth.status === "authenticated") {
    const search = new URLSearchParams(location.search);
    if (signup) {
      if (!session) return null;
      // Mid-signup (unverified, or no org yet): not an existing account. RequireVerified /
      // RequireOrg below "/" send them to /verify or /onboarding, without the notice.
      if (!session.emailVerified || !session.hasOrg) return <Navigate to="/" replace />;
      return <ExistingAccountRedirect email={session.me.email} />;
    }
    // A signed-in visitor who arrived with a plan came to buy, not to log in again: keep the
    // choice and send them to checkout, which shows an org that already pays its plan
    // instead of a payment form. Both values are validated against the catalog, never
    // interpolated raw.
    const plan = search.get("plan");
    if (isKnownPlan(plan)) {
      const interval = search.get("interval");
      const iv = isKnownInterval(interval) ? interval : "month";
      return <Navigate to={`/checkout?plan=${plan}&interval=${iv}`} replace />;
    }
    // Read side. Every read of `next` goes through safeNext, including this one — a guard
    // that skipped it would be the open redirect the function exists to prevent.
    const next = safeNext(search.get("next"));
    return <Navigate to={next} replace />;
  }
  return <>{children}</>;
}
